import type { LocationsGeoFilterInput, OffersQueryVariables } from '@ocx/graphql';

import { GEO_BUCKET_PRECISION } from './offers-cache.constants';

const round = (value: number, precision: number): number => {
  const factor = 10 ** precision;
  return Math.round(value * factor) / factor;
};

/**
 * `JSON.stringify` with object keys sorted alphabetically at every level so the same
 * logical input always produces the same string regardless of property insertion order.
 * Used for cache keys (localforage + Apollo keyArgs) so a future spread reorder can't
 * silently invalidate every cached entry.
 *
 * Arrays preserve order; `undefined` properties are dropped (matching `JSON.stringify`).
 */
export const stableStringify = (value: unknown): string => {
  if (value === null || typeof value !== 'object') return JSON.stringify(value);
  if (Array.isArray(value)) return `[${value.map(stableStringify).join(',')}]`;
  const obj = value as Record<string, unknown>;
  const keys = Object.keys(obj)
    .filter((k) => obj[k] !== undefined)
    .sort();
  return `{${keys.map((k) => `${JSON.stringify(k)}:${stableStringify(obj[k])}`).join(',')}}`;
};

/**
 * Quantize a precise geo filter for cache-key purposes only.
 * Never sent to the server — only used to build stable keys so that GPS jitter
 * doesn't produce a new key on every session.
 */
export const bucketGeoFilter = (
  filter: LocationsGeoFilterInput | null | undefined,
  precision: number = GEO_BUCKET_PRECISION,
): LocationsGeoFilterInput | null | undefined => {
  if (!filter) return filter;
  return {
    ...filter,
    latitude: round(filter.latitude, precision),
    longitude: round(filter.longitude, precision),
  };
};

/** Apply geo bucketing inside top-level `OffersQueryVariables` (used for the localforage key). */
export const bucketGeoInQueryVariables = (vars: OffersQueryVariables): OffersQueryVariables => ({
  ...vars,
  locationsGeoFilter: bucketGeoFilter(vars.locationsGeoFilter) ?? null,
});

/**
 * Apply geo bucketing inside Apollo's `Membership.offers` field arguments,
 * where the filter is nested under `args.filter.locationsGeoFilter`.
 */
export type OffersFieldArgs = {
  filter?: { locationsGeoFilter?: LocationsGeoFilterInput | null } & Record<string, unknown>;
} & Record<string, unknown>;

export const bucketGeoInFieldArgs = (args: OffersFieldArgs): OffersFieldArgs => {
  const { filter } = args;
  if (!filter?.locationsGeoFilter) return args;
  return {
    ...args,
    filter: { ...filter, locationsGeoFilter: bucketGeoFilter(filter.locationsGeoFilter) ?? null },
  };
};
